import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend, PieChart, Pie, Cell } from "recharts";
import { fmtDate } from "../../lib/constants";

const Panel = ({ title, sub, children, className = "", testId }) => (
  <div data-testid={testId} className={`glass rounded-3xl p-5 sm:p-6 rise ${className}`}>
    <h3 className="text-lg font-bold text-emerald-950">{title}</h3>
    {sub && <p className="text-xs text-slate-500">{sub}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

const tip = { contentStyle: { borderRadius: 14, border: "1px solid #e2e8f0", background: "rgba(255,255,255,0.95)", fontSize: 12 } };
const short = (h) => h.replace(" by Tamanna Hotels", "").replace(" Tamanna", "");

export const Charts = ({ data }) => {
  const pie = [
    { name: "Positive", value: data.kpis.sentiment_counts.Positive, c: "#10b981" },
    { name: "Neutral", value: data.kpis.sentiment_counts.Neutral, c: "#f59e0b" },
    { name: "Negative", value: data.kpis.sentiment_counts.Negative, c: "#ef4444" },
  ];
  const maxTopic = Math.max(1, ...data.topics.map((t) => t.count));
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Panel title="Rating trend" sub="Daily average rating and volume" className="lg:col-span-2" testId="chart-trend">
        <div className="h-64">
          <ResponsiveContainer>
            <AreaChart data={data.trend.map((t) => ({ ...t, label: fmtDate(t.date).slice(0, 6) }))}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} stroke="#94a3b8" width={28} />
              <Tooltip {...tip} />
              <Area type="monotone" dataKey="avg" name="Avg rating" stroke="#047857" strokeWidth={2.5} fill="url(#g1)" />
              <Area type="monotone" dataKey="count" name="Entries" stroke="#d97706" strokeWidth={2} fill="transparent" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel title="Sentiment mix" sub="AI-tagged tone" testId="chart-sentiment">
        <div className="h-64">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {pie.map((p) => <Cell key={p.name} fill={p.c} />)}
              </Pie>
              <Tooltip {...tip} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel title="Property comparison" sub="Restaurant vs Room Stay average rating" className="lg:col-span-2" testId="chart-hotels">
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={data.hotels.map((h) => ({ ...h, name: short(h.hotel) }))} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} stroke="#94a3b8" width={28} />
              <Tooltip {...tip} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="restaurant_avg" name="Restaurant" fill="#064e3b" radius={[6, 6, 0, 0]} />
              <Bar dataKey="room_avg" name="Room Stay" fill="#f59e0b" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel title="Topic clusters" sub="AI-extracted from transcripts & remarks" testId="chart-topics">
        <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
          {data.topics.length === 0 && <div className="text-sm text-slate-500">No topics yet</div>}
          {data.topics.map((t) => (
            <div key={t.topic} data-testid={`topic-${t.topic.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex justify-between text-sm"><span className="font-semibold text-emerald-950">{t.topic}</span><span className="text-slate-500">{t.count}</span></div>
              <div className="mt-1 flex h-2 overflow-hidden rounded-full bg-slate-100" style={{ width: `${(t.count / maxTopic) * 100}%` }}>
                <div className="bg-emerald-500" style={{ flex: t.Positive }} />
                <div className="bg-amber-400" style={{ flex: t.Neutral }} />
                <div className="bg-red-500" style={{ flex: t.Negative }} />
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
};
